import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApiError, api, atLeast, type Bench } from "../api";
import { Notice, Section } from "./Look";
import { Banner, BenchDialog, DeleteDialog } from "../pages/Benches";

/**
 * A bench's administration: rename, a new token, delete - on the bench's own
 * Setup tab (step K5, 2026-09-28).
 *
 * These were five buttons on every row of the bench LIST (Open, Runs, Edit,
 * New token, Delete: 20 buttons for 4 benches), the place people go to find a
 * bench, not to reconfigure one. What a reader may do is still decided by the
 * server's role: maintainer edits and rotates, owner deletes.
 */
export default function BenchAdmin({ bench, onChanged }: { bench: Bench; onChanged: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  if (!atLeast(bench.role, "maintainer")) return null;

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title={t("benchAdmin.title")} note={t("benchAdmin.note")}>
      {error && <Notice tone="error">{error}</Notice>}
      {token && (
        <Banner tone="token">
          <div>
            <Trans i18nKey="benches.tokenNote" values={{ name: bench.name }} components={{ b: <b /> }} />
          </div>
          <code className="mt-1 block break-all rounded bg-black/10 px-2 py-1 text-xs dark:bg-white/10">{token}</code>
        </Banner>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-quiet" onClick={() => setEditing(true)} disabled={busy}>{t("benches.edit")}</button>
        <button
          className="btn-quiet"
          disabled={busy}
          onClick={() => act(async () => setToken((await api.rotateToken(bench.id)).token))}
        >
          {bench.registered ? t("benches.newToken") : t("benches.getToken")}
        </button>
        {atLeast(bench.role, "owner") && (
          <button className="btn-danger" onClick={() => setDeleting(true)} disabled={busy}>{t("common.delete")}</button>
        )}
      </div>
      {editing && (
        <BenchDialog
          title={t("benches.editTitle", { name: bench.name })}
          initialName={bench.name}
          initialDescription={bench.description}
          onClose={() => setEditing(false)}
          onSubmit={(name, desc) =>
            act(async () => {
              await api.updateBench(bench.id, { name, description: desc });
              setEditing(false);
              onChanged();
            })
          }
        />
      )}
      {deleting && (
        <DeleteDialog
          bench={bench}
          onClose={() => setDeleting(false)}
          onConfirm={(typed) =>
            act(async () => {
              await api.deleteBench(bench.id, typed);
              navigate("/benches");
            })
          }
        />
      )}
    </Section>
  );
}
